// components/CaseTagModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { X, Upload, Trash2 } from 'lucide-react';
import authAxios from '../utils/authAxios';
import SecureImage from './SecureImage';
import TypeaheadSelect from './TypeaheadSelect';
import { Input, Button, DangerButton } from '../styles/TableStyles';
import { SizeTag } from '../styles/CustomerSpecsIndexStyles';
import { ALL_CUSTOMERS_VALUE } from '../utils/caseTagScope';

export const CASETAG_FOLDER = 'CustomerSpecs/CaseTag';

// Reduce a stored ImageLink (bare name, foldered path, or full URL) to a bare filename.
export function toFilename(link) {
  if (!link) return '';
  let s = String(link).split('?')[0].split('#')[0];
  s = s.split('/').pop().split('\\').pop();
  try { s = decodeURIComponent(s); } catch { /* leave as-is */ }
  return s.trim();
}

// Sentinel-aware CSV -> chip list ('All' or blank collapses to the sentinel).
function listFromCsv(csv) {
  const s = String(csv ?? '').trim();
  if (!s || s === ALL_CUSTOMERS_VALUE) return [ALL_CUSTOMERS_VALUE];
  return s.split(',').map((t) => t.trim()).filter(Boolean);
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: var(--shadow-a35);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Dialog = styled.div`
  background: var(--white);
  border-radius: 16px;
  box-shadow: 0 24px 64px var(--shadow-a25);
  width: min(520px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 22px 22px 18px;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-a60);
  cursor: pointer;

  &:hover { background: var(--shadow-a05); color: var(--black); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
`;

const Title = styled.h2`
  margin: 0 0 14px;
  font-size: 1.05rem;
  color: var(--green-800);
  font-weight: 700;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--ink-a70);
  margin-bottom: 5px;
`;

const ImageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const ThumbWrap = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
`;

const thumbStyle = {
  width: '120px',
  height: '120px',
  objectFit: 'cover',
  borderRadius: '8px',
  border: '1px solid var(--shadow-a08)',
  display: 'block',
};

const RemoveBtn = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: var(--red-600);
  color: var(--white);
  border: 2px solid var(--white);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px var(--shadow-a25);

  &:hover { background: var(--color-danger); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
`;

const NoImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 8px;
  border: 1px dashed var(--shadow-a15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-a45);
  font-size: 0.82rem;
`;

const UploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  background: var(--green-800);
  color: var(--white);
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover { opacity: 0.92; }
`;

const ErrorText = styled.p`
  margin: 0 0 12px;
  color: var(--color-danger);
  font-size: 0.85rem;
  font-weight: 600;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

// A removable chip rendered as a real <button> so it is keyboard-operable
// (mirrors the BagLabelModal RemovableTag — click removes, no + glyph).
const RemovableTag = ({ label, onRemove, disabled }) => (
  <SizeTag
    as="button"
    type="button"
    aria-label={`Remove ${label}`}
    onClick={disabled ? undefined : onRemove}
    style={{ cursor: disabled ? 'default' : 'pointer', background: 'var(--grey-600)' }}
  >
    {label}
  </SizeTag>
);

export default function CaseTagModal({
  open,
  mode = 'add', // 'add' | 'edit'
  row,
  customers = [],
  imgVersion = 0,
  onClose,
  onSaved,
  onRequestDelete,
  busy = false,
}) {
  const [name, setName] = useState('');
  const [customerIds, setCustomerIds] = useState([ALL_CUSTOMERS_VALUE]);
  const [stagedFile, setStagedFile] = useState(null);
  const [stagedPreview, setStagedPreview] = useState(null);
  const [removeExisting, setRemoveExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const previewRef = useRef(null);

  // Initialize whenever the modal opens for a (new) row.
  useEffect(() => {
    if (!open) return;
    setName(row?.CaseTagName ?? '');
    setCustomerIds(listFromCsv(row?.CustomersCsv));
    setStagedFile(null);
    setStagedPreview(null);
    setRemoveExisting(false);
    setSaving(false);
    setError('');
  }, [open, row]);

  // Revoke the local preview object URL when it changes / unmounts.
  useEffect(() => {
    previewRef.current = stagedPreview;
    return () => { if (previewRef.current) URL.revokeObjectURL(previewRef.current); };
  }, [stagedPreview]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const existingFilename = useMemo(() => toFilename(row?.ImageLink), [row]);
  const hasExisting = mode === 'edit' && existingFilename && !removeExisting && !stagedFile;

  const customerNameById = useMemo(() => {
    const m = new Map();
    for (const c of customers || []) m.set(String(c.UniqueID), String(c.Customer || ''));
    return m;
  }, [customers]);

  const customerOptions = useMemo(() => {
    const selected = new Set(customerIds);
    const opts = [];
    if (!selected.has(ALL_CUSTOMERS_VALUE)) {
      opts.push({ value: ALL_CUSTOMERS_VALUE, label: 'All Customers' });
    }
    for (const c of customers || []) {
      const v = String(c.UniqueID);
      if (!selected.has(v)) opts.push({ value: v, label: String(c.Customer || '') });
    }
    return opts;
  }, [customers, customerIds]);

  if (!open) return null;

  // Sentinel exclusivity: picking "All Customers" replaces the list, picking a specific
  // customer drops the sentinel, and an emptied list snaps back to the sentinel.
  const addCustomer = (v) => {
    const val = String(v || '').trim();
    if (!val) return;
    setCustomerIds((prev) => (
      val === ALL_CUSTOMERS_VALUE
        ? [ALL_CUSTOMERS_VALUE]
        : Array.from(new Set([...prev.filter((x) => x !== ALL_CUSTOMERS_VALUE), val]))
    ));
  };
  const removeCustomer = (val) => {
    setCustomerIds((prev) => {
      const rest = prev.filter((x) => x !== val);
      return rest.length ? rest : [ALL_CUSTOMERS_VALUE];
    });
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (stagedPreview) URL.revokeObjectURL(stagedPreview);
    setStagedFile(file);
    setStagedPreview(URL.createObjectURL(file));
    setRemoveExisting(false);
  };

  const clearStaged = () => {
    if (stagedPreview) URL.revokeObjectURL(stagedPreview);
    setStagedFile(null);
    setStagedPreview(null);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) { setError('Case Tag Name is required'); return; }
    setError('');
    setSaving(true);
    try {
      const csv = customerIds.includes(ALL_CUSTOMERS_VALUE) || customerIds.length === 0
        ? ALL_CUSTOMERS_VALUE
        : customerIds.join(',');
      const fd = new FormData();
      fd.append('CaseTagName', trimmed);
      fd.append('CustomersCsv', csv);
      if (stagedFile) fd.append('image', stagedFile);
      else if (removeExisting) fd.append('removeImage', 'true');

      const res = mode === 'edit'
        ? await authAxios.put(`/api/casetags/${row.UniqueID}`, fd)
        : await authAxios.post('/api/casetags', fd);

      // Reset before notifying so the saved/false invariant holds regardless of
      // whether the parent unmounts the modal.
      setSaving(false);
      onSaved && onSaved({
        oldImageLink: row?.ImageLink ?? null,
        newImageLink: res?.data?.ImageLink ?? null,
      });
    } catch (err) {
      console.error('Save Case Tag failed', err);
      const status = err?.response?.status;
      if (status === 413) setError('Image too large (max 10 MB).');
      else if (status === 400) setError(err?.response?.data?.error || 'Invalid input.');
      else setError('Could not save case tag.');
      setSaving(false);
    }
  };

  const disabled = saving || busy;

  return (
    <Backdrop onClick={onClose} role="presentation">
      <Dialog onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="casetag-modal-title">
        <CloseBtn type="button" onClick={onClose} aria-label="Close dialog">
          <X size={16} aria-hidden="true" />
        </CloseBtn>

        <Title id="casetag-modal-title">{mode === 'edit' ? 'Edit Case Tag' : 'Add Case Tag'}</Title>

        <form onSubmit={handleSave}>
          <Field>
            <Label htmlFor="casetag-name">Case Tag Name</Label>
            <Input
              id="casetag-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Walmart Bag"
              autoFocus
            />
          </Field>

          <Field>
            <Label>Customers</Label>
            <TypeaheadSelect
              ariaLabel="Add customer"
              value=""
              placeholder="Add customer..."
              size="md"
              options={customerOptions}
              onChange={addCustomer}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {customerIds.map((id) => (
                <RemovableTag
                  key={id}
                  label={id === ALL_CUSTOMERS_VALUE ? 'All Customers' : (customerNameById.get(id) || `#${id}`)}
                  onRemove={() => removeCustomer(id)}
                  disabled={disabled}
                />
              ))}
            </div>
          </Field>

          <Field>
            <Label>Image</Label>
            <ImageRow>
              {stagedFile ? (
                <ThumbWrap>
                  <img src={stagedPreview} alt="New preview" style={thumbStyle} />
                  <RemoveBtn type="button" onClick={clearStaged} aria-label="Remove selected image">
                    <X size={14} aria-hidden="true" />
                  </RemoveBtn>
                </ThumbWrap>
              ) : hasExisting ? (
                <ThumbWrap>
                  <SecureImage
                    key={`modal-${row?.UniqueID}-${imgVersion}`}
                    src={existingFilename}
                    folder={CASETAG_FOLDER}
                    alt={name}
                    style={thumbStyle}
                  />
                  <RemoveBtn type="button" onClick={() => setRemoveExisting(true)} aria-label="Remove current image">
                    <X size={14} aria-hidden="true" />
                  </RemoveBtn>
                </ThumbWrap>
              ) : (
                <NoImage>No image</NoImage>
              )}

              <UploadLabel>
                <Upload size={16} aria-hidden="true" />
                {stagedFile || hasExisting ? 'Change Image' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={pickFile}
                  style={{ display: 'none' }}
                />
              </UploadLabel>
            </ImageRow>
          </Field>

          {error && <ErrorText>{error}</ErrorText>}

          <Foot>
            {mode === 'edit' && (
              <DangerButton type="button" onClick={() => onRequestDelete && onRequestDelete(row)} disabled={disabled}>
                <Trash2 size={15} aria-hidden="true" /> Delete
              </DangerButton>
            )}
            <div style={{ flex: 1 }} />
            <Button type="button" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={disabled}>{saving ? 'Saving…' : 'Save'}</Button>
          </Foot>
        </form>
      </Dialog>
    </Backdrop>
  );
}
